import React, { useContext } from 'react';

import { Helmet, BasePage, getHelpLink, StaticContext } from '@sm/webassets';
import { Typography } from '@sm/wds-react';
import { IconArrowLeft } from '@sm/wds-icons';
import { defineMessages, t, T } from '@sm/intl';

import EmailPreferencesQuery from './EmailPreferencesQuery';
import UserAccountBasePage from '../components/UserAccountBasePage';
import HelpSideBar from '../components/UserAccountBasePage/HelpSideBar';

import './marketing-email-preferences.scss';

const COPY = defineMessages({
  PAGE_TITLE: {
    id: 'User.MarketingEmailPreferences.PageTitle',
    defaultMessage: 'Email Preferences',
    description: '[Type: Header][Vis.: high] - Marketing email preferences page title',
  },
  BREADCRUMB_LINK: {
    id: 'User.MarketingEmailPreferences.BreadcrumbLink',
    defaultMessage: 'Back to Account Summary',
    description: '[Type: Header][Vis.: med] - Link to return to Account Summary',
  },
  HELP_LINK_OPT_OUT: {
    id: 'User.MarketingEmailPreferences.OptOutHelpLink',
    defaultMessage: 'Opt out of emails and newsletters',
    description: '[Type: Link][Vis.: med] - Marketing email opt out help link',
  },
  HELP_LINK_CHANGE_EMAIL: {
    id: 'User.MarketingEmailPreferences.ChangeEmailHelpLink',
    defaultMessage: 'Change your account email',
    description: '[Type: Link][Vis.: med] - Change email help link',
  },
  HELP_LINK_HELP_CENTER: {
    id: 'User.MarketingEmailPreferences.HelpCenterLink',
    defaultMessage: 'Browse our Help Center',
    description: '[Type: Link][Vis.: med] - Help center link',
  },
  HELP_SIDE_BAR_HEADER: {
    id: 'User.MarketingEmailPreferences.HelpSideBarTitle',
    defaultMessage: 'Learn more',
    description: '[Type: Header][Vis.: med] - Help side bar card header',
  },
});

const getSideBarHelpLinks = langCode => {
  return [
    {
      key: 'optOut',
      text: t(COPY.HELP_LINK_OPT_OUT),
      path: getHelpLink(langCode, {
        helpName: 'opt-out',
        params: { ut_source: 'email_unsubscribe' },
      }),
    },
    {
      key: 'changeEmail',
      text: t(COPY.HELP_LINK_CHANGE_EMAIL),
      path: getHelpLink(langCode, {
        helpName: 'How-do-I-change-the-username-email-address-on-the-account',
        params: { ut_source: 'email_unsubscribe' },
      }),
    },
    {
      key: 'helpCenter',
      text: t(COPY.HELP_LINK_HELP_CENTER),
      path: getHelpLink(langCode, {
        params: { ut_source: 'email_unsubscribe' },
      }),
    },
  ];
};

const UserEmailPreferencesEntry = () => {
  const { pageOptions, user } = useContext(StaticContext);

  const sideBar = <HelpSideBar title={t(COPY.HELP_SIDE_BAR_HEADER)} helpLinks={getSideBarHelpLinks(user.languageId)} />;

  return (
    <BasePage pageId="UserEmailPreferences" legacyWeb="userweb" sticky={false}>
      <Helmet>
        <title>{t(COPY.PAGE_TITLE)}</title>
      </Helmet>
      <Choose>
        <When condition={pageOptions.origin === 'my_account'}>
          <UserAccountBasePage activePage="myAccount" sideBar={sideBar}>
            <Typography variant="bodySm" className="wds-m-b-3">
              <a href="/user/account">
                <span className="wds-m-r-1">
                  <IconArrowLeft size="sm" />
                </span>
                <T desc={COPY.BREADCRUMB_LINK} />
              </a>
            </Typography>
            <EmailPreferencesQuery />
          </UserAccountBasePage>
        </When>
        <Otherwise>
          <div className="sm-marketing-email-preferences" data-testid="MarketingEmailPreferences">
            <div className="sm-marketing-email-preferences__content wds-p-t-6 wds-p-b-8">
              <EmailPreferencesQuery />
            </div>
          </div>
        </Otherwise>
      </Choose>
    </BasePage>
  );
};

export default UserEmailPreferencesEntry;
